import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UserCog, Search } from "lucide-react";
import {
  AssetRoleMemberView,
  getAssetRoleMembers,
  grantAssetRole,
  revokeAssetRole,
} from "../../../../api/General/assets";
import { Switch } from "@/components/ui/Switch";
import { Spinner } from "@/components/ui/Spinner";
import { useToast, ToastRenderer } from "@/hooks/useToast";

export default function AssetRolesTab() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [members, setMembers] = useState<AssetRoleMemberView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounce the search box
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [search]);

  const load = useCallback(async (query: string) => {
    setLoading(true);
    setError(false);
    try {
      const result = await getAssetRoleMembers(query || undefined);
      if (result.errors.length === 0) {
        setMembers(result.payload);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(debouncedSearch);
  }, [debouncedSearch, load]);

  const handleToggle = async (member: AssetRoleMemberView, next: boolean) => {
    setPendingUserId(member.userId);
    // Optimistic update
    setMembers((prev) => prev.map((m) => (m.userId === member.userId ? { ...m, isAssetManager: next } : m)));
    try {
      const result = next ? await grantAssetRole(member.userId) : await revokeAssetRole(member.userId);
      if (result.errors.length === 0) {
        showToast(next ? t("assetRoles.granted") : t("assetRoles.revoked"), "success");
      } else {
        // Roll back on failure
        setMembers((prev) => prev.map((m) => (m.userId === member.userId ? { ...m, isAssetManager: !next } : m)));
        showToast(result.errors[0]?.description || t("Update failed"), "error");
      }
    } catch {
      setMembers((prev) => prev.map((m) => (m.userId === member.userId ? { ...m, isAssetManager: !next } : m)));
      showToast(t("Update failed"), "error");
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-base font-semibold text-foreground">{t("assetRoles.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("assetRoles.subtitle")}</p>
      </div>

      {/* Server authorization is TOKEN-CLAIM based (OpenIddict bakes roles into the access
          token at sign-in). Refreshing `state.user.isAssetManager` from GetUserInfo updates
          this client's UI but NOT the target user's token, so a freshly granted/revoked user
          keeps their old effective access until they sign in again. Say so. */}
      <p className="text-sm text-muted-foreground" data-testid="asset-roles-relogin-note">
        {t("assetRoles.reloginNote")}
      </p>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("assetRoles.searchPlaceholder")}
          className="w-full ps-9 pe-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          data-testid="asset-roles-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12" data-testid="asset-roles-error">
          <p className="text-sm text-destructive">{t("assetRoles.loadError")}</p>
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12" data-testid="asset-roles-empty">
          <UserCog className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">{t("assetRoles.noResults")}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetRoles.colEmployee")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetRoles.colDepartment")}
                </th>
                <th className="px-4 py-3 text-end text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetRoles.colAccess")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {members.map((m) => (
                <tr key={m.userId} className="hover:bg-muted/30 transition-colors" data-testid={`asset-role-row-${m.userId}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary">
                          {(m.fullName || "?").charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{m.fullName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{m.department || "—"}</td>
                  <td className="px-4 py-3 text-end">
                    {/* Hr has asset access WITHOUT holding the tag. Revoking a tag they do not
                        hold is a silent no-op, so offer no toggle — just state where the
                        access comes from. */}
                    {m.isImplicitAssetManager ? (
                      <span className="text-xs font-medium text-success" data-testid={`asset-role-implicit-${m.userId}`}>
                        {t("assetRoles.viaHr")}
                      </span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <span className={`text-xs font-medium ${m.isAssetManager ? "text-success" : "text-muted-foreground"}`}>
                          {m.isAssetManager ? t("assetRoles.granted") : t("assetRoles.notGranted")}
                        </span>
                        <Switch
                          checked={m.isAssetManager}
                          onCheckedChange={(checked) => handleToggle(m, checked)}
                          disabled={pendingUserId === m.userId}
                          id={`asset-role-switch-${m.userId}`}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
